import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import {
  useAdminLogoutMutation,
  useGetSystemNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkSystemNotificationReadMutation,
  useGlobalSearchQuery,
} from '../../store/api/adminApi';
import { logout } from '../../store/slices/authSlice';
import { useAppDispatch } from '../../hooks/redux';
import { confirmLogout } from '../../utils/confirm';
import { showSuccess, showError } from '../../utils/toast';
import { useDropdown } from '../../hooks/useDropdown';
import { getAppType, setAppType, APP_TYPE_LABELS, AppType } from '../../utils/appType';
import { useTheme } from '../../context/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSun, faMoon, faBell, faBoxOpen, faTicketAlt, faCircle, faSearch, faUser, faBullhorn, faFlag, faTimes } from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/images/logo.png';

const timeAgo = (dateStr: string): string => {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const typeIcon: Record<string, any> = {
  new_ad:            faBoxOpen,
  voucher_activated: faTicketAlt,
};

// ─── Global Search ────────────────────────────────────────────────────────────

interface SearchResult {
  id: number;
  type: 'user' | 'ad' | 'report_ad' | 'user_report';
  title: string;
  subtitle?: string;
  status?: string;
  picture?: string;
  image?: string;
  user?: string;
  reporter?: string;
  reported_user?: string;
}

interface SearchResults {
  users: SearchResult[];
  ads: SearchResult[];
  reports: SearchResult[];
}

const resultTypeIcon: Record<string, any> = {
  user:        faUser,
  ad:          faBullhorn,
  report_ad:   faFlag,
  user_report: faFlag,
};

const statusBadgeClass: Record<string, string> = {
  approved:   'badge-light-success',
  pending:  'badge-light-warning',
  rejected: 'badge-light-danger',
  draft: 'badge-light-secondary text-dark',
};

function useDebounce(value: string, delay: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  const { data, isFetching } = useGlobalSearchQuery(debouncedQuery, {
    skip: debouncedQuery.trim().length < 2,
  });

  const results: SearchResults = data?.data ?? { users: [], ads: [], reports: [] };
  const hasResults =
    results.users.length > 0 || results.ads.length > 0 || results.reports.length > 0;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Open dropdown as soon as we have a query of 2+ chars
  useEffect(() => {
    setOpen(debouncedQuery.trim().length >= 2);
  }, [debouncedQuery]);

  const handleResultClick = (result: SearchResult) => {
    setQuery('');
    setOpen(false);
    if (result.type === 'user') navigate(`/admin/users/${result.id}`);
    else if (result.type === 'ad') navigate(`/admin/ads/${result.id}`);
    else navigate('/admin/reviews');
  };

  const clearSearch = () => {
    setQuery('');
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div
      ref={wrapperRef}
      className="app-navbar-item ms-1 ms-lg-3 position-relative"
      style={{ minWidth: 220 }}
    >
      {/* Input */}
      <div
        className="d-flex align-items-center rounded px-3"
        style={{
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.2)',
          height: 38,
          gap: 8,
          transition: 'background 0.2s',
        }}
      >
        <FontAwesomeIcon icon={faSearch} className="text-white" style={{ fontSize: 13, opacity: 0.8, flexShrink: 0 }} />
        <input
          ref={inputRef}
          className='form-control'
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (debouncedQuery.trim().length >= 2) setOpen(true); }}
          placeholder="Search users, ads, reports…"
          style={{
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#fff',
            fontSize: 13,
            width: '100%',
            minWidth: 0,
          }}
        />
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', lineHeight: 1 }}
          >
            <FontAwesomeIcon icon={faTimes} className="text-white" style={{ fontSize: 11, opacity: 0.7 }} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className="menu menu-sub menu-sub-dropdown"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            minWidth: 340,
            maxWidth: 420,
            zIndex: 1060,
            display: 'block',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}
          onMouseDown={(e) => e.preventDefault()}
        >
          {isFetching ? (
            <div className="d-flex align-items-center justify-content-center py-5 gap-2">
              <span className="spinner-border spinner-border-sm text-primary" />
              <span className="text-muted fs-7">Searching…</span>
            </div>
          ) : !hasResults ? (
            <div className="d-flex flex-column align-items-center justify-content-center py-8">
              <FontAwesomeIcon icon={faSearch} style={{ fontSize: 28, opacity: 0.12 }} className="text-muted mb-2" />
              <span className="text-muted fs-7">No results for "{debouncedQuery}"</span>
            </div>
          ) : (
            <>
              {results.users.length > 0 && (
                <SearchSection
                  label="Users"
                  icon={faUser}
                  items={results.users}
                  onSelect={handleResultClick}
                />
              )}
              {results.ads.length > 0 && (
                <SearchSection
                  label="Ads"
                  icon={faBullhorn}
                  items={results.ads}
                  onSelect={handleResultClick}
                />
              )}
              {results.reports.length > 0 && (
                <SearchSection
                  label="Reports"
                  icon={faFlag}
                  items={results.reports}
                  onSelect={handleResultClick}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const SearchSection: React.FC<{
  label: string;
  icon: any;
  items: SearchResult[];
  onSelect: (r: SearchResult) => void;
}> = ({ label, icon, items, onSelect }) => (
  <div>
    {/* Section header */}
    <div
      className="d-flex align-items-center gap-2 px-4 py-2"
      style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'rgba(0,0,0,0.02)' }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 11, color: '#396A54' }} />
      <span className="text-uppercase fw-bold fs-9 text-muted" style={{ letterSpacing: '0.06em' }}>{label}</span>
    </div>
    {items.map((item) => (
      <SearchResultRow key={`${item.type}-${item.id}`} item={item} onSelect={onSelect} />
    ))}
  </div>
);

const SearchResultRow: React.FC<{ item: SearchResult; onSelect: (r: SearchResult) => void }> = ({ item, onSelect }) => {
  const avatar = item.type === 'user' ? item.picture : item.image;
  const icon = resultTypeIcon[item.type] ?? faSearch;

  return (
    <div
      className="d-flex align-items-center gap-3 px-4 py-3 search-result-row"
      style={{ cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.04)', transition: 'background 0.12s' }}
      onClick={() => onSelect(item)}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(57,106,84,0.06)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {/* Avatar / Icon */}
      {/* <div
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 overflow-hidden"
        style={{ width: 34, height: 34, background: 'rgba(57,106,84,0.1)' }}
      >
        {avatar ? (
          <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <FontAwesomeIcon icon={icon} style={{ fontSize: 14, color: '#396A54' }} />
        )}
      </div> */}

      {/* Text */}
      <div className="flex-grow-1 overflow-hidden">
        <div className="fw-semibold text-dark fs-7 text-truncate">{item.title}</div>
        {item.subtitle && (
          <div className="text-muted fs-8 text-truncate">{item.subtitle}</div>
        )}
      </div>

      {/* Status badge */}
      {item.status && (
        <span
          className={`badge ${statusBadgeClass[item.status?.toLowerCase()] ?? 'badge-light-secondary'} fs-9 flex-shrink-0`}
        >
          {item.status}
        </span>
      )}
    </div>
  );
};

// ─── AdminTopbar ──────────────────────────────────────────────────────────────

const AdminTopbar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const [logoutMutation] = useAdminLogoutMutation();
  const profileDropdown = useDropdown();
  const notificationDropdown = useDropdown();
  const appTypeDropdown = useDropdown();
  const [currentAppType, setCurrentAppType] = useState<AppType>(getAppType());
  const { isDark, toggleTheme } = useTheme();

  const { data: unreadData } = useGetUnreadNotificationCountQuery(undefined, {
    pollingInterval: 30000,
  });
  const { data: notifData, refetch: refetchNotifs } = useGetSystemNotificationsQuery(
    { per_page: 10 },
    { skip: !notificationDropdown.isOpen }
  );
  const [markRead] = useMarkSystemNotificationReadMutation();

  const unreadCount: number = unreadData?.unread_count ?? 0;
  const notifications: any[] = notifData?.data?.data ?? [];

  useEffect(() => {
    setCurrentAppType(getAppType());
  }, []);

  useEffect(() => {
    if (notificationDropdown.isOpen) {
      refetchNotifs();
    }
  }, [notificationDropdown.isOpen]);

  const handleAppTypeChange = (appType: AppType) => {
    setAppType(appType);
    setCurrentAppType(appType);
    appTypeDropdown.close();
    if (appType === 'education') window.location.href = '/admin/dashboard';
    if (appType === 'trade') window.location.href = '/admin/trade-dashboard';
  };

  const handleLogout = async () => {
    const confirmed = await confirmLogout();
    if (confirmed) {
      try {
        await logoutMutation(undefined).unwrap();
        dispatch(logout());
        showSuccess('Logged out successfully!');
        navigate('/admin/login');
      } catch (err: any) {
        showError(err?.data?.message || 'Logout failed. Please try again.');
      }
    }
  };

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await markRead('all');
  };

  const handleNotifClick = async (notif: any) => {
    if (!notif.is_read) {
      await markRead(notif.id);
    }
    notificationDropdown.close();
    if (notif.type === 'new_ad' && notif.reference_id) {
      navigate(`/admin/ads/${notif.reference_id}`);
    }
  };

  return (
    <div id="kt_app_header" className="app-header bg-gradient-right bg-base">
      <div
        className="app-container container-fluid d-flex align-items-stretch justify-content-between"
        id="kt_app_header_container"
      >
        {/* Left side */}
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center d-lg-none ms-n2 me-2" title="Show sidebar menu">
            <div
              className="btn btn-icon btn-active-color-primary w-35px h-35px"
              id="kt_app_sidebar_mobile_toggle"
            >
              <span className="svg-icon svg-icon-1">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z" fill="currentColor" />
                  <path opacity="0.3" d="M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z" fill="currentColor" />
                </svg>
              </span>
            </div>
          </div>

          {/* App Type Dropdown */}
          <div className="app-navbar-item position-relative me-3">
            <div
              ref={appTypeDropdown.triggerRef as React.RefObject<HTMLDivElement>}
              className="btn btn-sm btn-light btn-active-light-primary d-flex align-items-center"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); appTypeDropdown.toggle(); }}
              style={{ cursor: 'pointer' }}
            >
              <span className="fw-semibold me-2">{APP_TYPE_LABELS[currentAppType]}</span>
              <span className="svg-icon svg-icon-5 svg-icon-gray-500">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11.4343 12.7344L7.25 8.55005C6.83579 8.13584 6.83579 7.46426 7.25 7.05005C7.66421 6.63584 8.33579 6.63584 8.75 7.05005L12.4343 10.7344C12.7467 11.0468 12.7467 11.5533 12.4343 11.8657L8.75 15.55C8.33579 15.9643 7.66421 15.9643 7.25 15.55C6.83579 15.1358 6.83579 14.4643 7.25 14.05L11.4343 12.7344Z" fill="currentColor" />
                </svg>
              </span>
            </div>
            {appTypeDropdown.isOpen && (
              <div
                ref={appTypeDropdown.dropdownRef}
                className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-color fw-semibold py-4 fs-6 w-200px"
                style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 1050, display: 'block' }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <div className="menu-item px-3">
                  <a
                    onClick={(e) => { e.preventDefault(); handleAppTypeChange('education'); }}
                    className={`menu-link px-5 ${currentAppType === 'education' ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                  >
                    {APP_TYPE_LABELS.education}
                  </a>
                </div>
                <div className="menu-item px-3">
                  <a
                    onClick={(e) => { e.preventDefault(); handleAppTypeChange('trade'); }}
                    className={`menu-link px-5 ${currentAppType === 'trade' ? 'active' : ''}`}
                    style={{ cursor: 'pointer' }}
                  >
                    {APP_TYPE_LABELS.trade}
                  </a>
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0">
            <Link to="/admin/dashboard" className="d-lg-none">
              <img alt="Logo" src={logo} className="h-30px" />
            </Link>
          </div>
        </div>

        {/* Right side */}
        <div
          className="d-flex align-items-stretch justify-content-between flex-lg-grow-1"
          id="kt_app_header_wrapper"
        >
          <div
            className="app-header-menu app-header-mobile-drawer align-items-stretch"
            data-kt-drawer="true"
            data-kt-drawer-name="app-header-menu"
            data-kt-drawer-activate="{default: true, lg: false}"
            data-kt-drawer-overlay="true"
            data-kt-drawer-width="250px"
            data-kt-drawer-direction="end"
            data-kt-drawer-toggle="#kt_app_header_menu_toggle"
            data-kt-swapper="true"
            data-kt-swapper-mode="{default: 'append', lg: 'prepend'}"
            data-kt-swapper-parent="{default: '#kt_app_body', lg: '#kt_app_header_wrapper'}"
          >
            <div
              className="menu menu-rounded menu-column menu-lg-row my-5 my-lg-0 align-items-stretch fw-semibold px-2 px-lg-0"
              id="kt_app_header_menu"
              data-kt-menu="true"
            />
          </div>

          <div className="app-navbar flex-shrink-0">

            {/* Global Search */}
            <GlobalSearch />

            {/* Dark Mode Toggle */}
            <div className="app-navbar-item ms-1 ms-lg-3">
              <button
                onClick={toggleTheme}
                className="btn btn-icon btn-custom w-35px h-35px w-md-40px h-md-40px"
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                style={{ border: 'none', background: 'transparent' }}
              >
                <FontAwesomeIcon icon={isDark ? faSun : faMoon} className="text-white" style={{ fontSize: 18 }} />
              </button>
            </div>

            {/* Bell Notifications */}
            <div className="app-navbar-item ms-1 ms-lg-3 position-relative">
              <div
                ref={notificationDropdown.triggerRef as React.RefObject<HTMLDivElement>}
                className="btn btn-icon btn-custom w-35px h-35px w-md-40px h-md-40px position-relative"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); notificationDropdown.toggle(); }}
                style={{ cursor: 'pointer', background: 'transparent', border: 'none' }}
              >
                <FontAwesomeIcon icon={faBell} className="text-white" style={{ fontSize: 20 }} />
                {unreadCount > 0 && (
                  <span
                    className="position-absolute top-25 start-75 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: 10, minWidth: 18, padding: '2px 5px', lineHeight: 1.4 }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>

              {notificationDropdown.isOpen && (
                <div
                  ref={notificationDropdown.dropdownRef}
                  className="menu menu-sub menu-sub-dropdown"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 4,
                    zIndex: 1050,
                    display: 'block',
                    width: 380,
                    maxWidth: '95vw',
                  }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  <div
                    className="d-flex align-items-center justify-content-between px-5 py-4"
                    style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold text-dark fs-5">Notifications</span>
                      {unreadCount > 0 && (
                        <span className="badge badge-circle badge-danger fs-9">{unreadCount}</span>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary py-1 px-3 fs-8"
                        onClick={handleMarkAllRead}
                      >
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div className="d-flex flex-column align-items-center justify-content-center py-12">
                        <FontAwesomeIcon icon={faBell} style={{ fontSize: 36, opacity: 0.15 }} className="text-muted mb-3" />
                        <span className="text-muted fs-7">No notifications yet</span>
                      </div>
                    ) : (
                      notifications.map((notif: any) => (
                        <div
                          key={notif.id}
                          className={`d-flex align-items-start gap-3 px-5 py-4 ${!notif.is_read ? 'bg-light-primary' : ''}`}
                          style={{
                            cursor: 'pointer',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            transition: 'background 0.15s',
                          }}
                          onClick={() => handleNotifClick(notif)}
                        >
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                            style={{ width: 38, height: 38, background: 'rgba(57,106,84,0.12)' }}
                          >
                            <FontAwesomeIcon
                              icon={typeIcon[notif.type] ?? faBell}
                              style={{ fontSize: 16, color: '#396A54' }}
                            />
                          </div>

                          <div className="flex-grow-1 overflow-hidden">
                            <div className="d-flex align-items-center justify-content-between gap-2">
                              <span className="fw-semibold text-dark fs-7 text-truncate">{notif.title}</span>
                              {!notif.is_read && (
                                <FontAwesomeIcon icon={faCircle} style={{ fontSize: 7, color: '#396A54', flexShrink: 0 }} />
                              )}
                            </div>
                            {notif.message && (
                              <div
                                className="text-muted fs-8 mt-1"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                              >
                                {notif.message}
                              </div>
                            )}
                            <div className="text-muted fs-9 mt-1">{timeAgo(notif.created_at)}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="text-center py-3" style={{ borderTop: '1px solid rgba(0,0,0,0.07)' }}>
                    <Link
                      to="/admin/ads?status=Pending"
                      className="text-primary fw-semibold fs-7"
                      onClick={() => notificationDropdown.close()}
                    >
                      View all pending ads →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div
              className="app-navbar-item ms-1 ms-lg-3 position-relative"
              id="kt_header_user_menu_toggle"
            >
              <div
                ref={profileDropdown.triggerRef as React.RefObject<HTMLDivElement>}
                className="cursor-pointer symbol symbol-35px symbol-md-40px"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); profileDropdown.toggle(); }}
                style={{ cursor: 'pointer' }}
              >
                <img src={user?.picture || '/media/avatars/blank.png'} alt="user" />
              </div>
              {profileDropdown.isOpen && (
                <div
                  ref={profileDropdown.dropdownRef}
                  className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-color fw-semibold py-4 fs-6 w-275px"
                  style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 1050, display: 'block' }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  <div className="menu-item px-3">
                    <div className="menu-content d-flex align-items-center px-3">
                      <div className="symbol symbol-50px me-5">
                        <img alt="Avatar" src={user?.picture || '/media/avatars/blank.png'} />
                      </div>
                      <div className="d-flex flex-column">
                        <div className="fw-bold d-flex align-items-center fs-5">{user?.name}</div>
                        <a href="#" className="fw-semibold text-muted text-hover-primary fs-7">{user?.email}</a>
                      </div>
                    </div>
                  </div>
                  <div className="separator my-2"></div>
                  <div className="menu-item px-5 my-1">
                    <Link to="/admin/account" className="menu-link px-5" onClick={() => profileDropdown.close()}>
                      Account Settings
                    </Link>
                  </div>
                  <div className="menu-item px-5">
                    <a
                      onClick={(e) => { e.preventDefault(); handleLogout(); }}
                      className="menu-link px-5"
                      style={{ cursor: 'pointer' }}
                    >
                      Logout
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="app-navbar-item d-lg-none ms-2 me-n3" title="Show header menu">
              <div
                className="btn btn-icon btn-active-color-primary w-35px h-35px"
                id="kt_app_header_menu_toggle"
              >
                <span className="svg-icon svg-icon-1">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M13 11H3C2.4 11 2 10.6 2 10V9C2 8.4 2.4 8 3 8H13C13.6 8 14 8.4 14 9V10C14 10.6 13.6 11 13 11ZM22 5V4C22 3.4 21.6 3 21 3H3C2.4 3 2 3.4 2 4V5C2 5.6 2.4 6 3 6H21C21.6 6 22 5.6 22 5Z" fill="currentColor" />
                    <path opacity="0.3" d="M21 16H3C2.4 16 2 15.6 2 15V14C2 13.4 2.4 13 3 13H21C21.6 13 22 13.4 22 14V15C22 15.6 21.6 16 21 16ZM14 20V19C14 18.4 13.6 18 13 18H3C2.4 18 2 18.4 2 19V20C2 20.6 2.4 21 3 21H13C13.6 21 14 20.6 14 20Z" fill="currentColor" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTopbar;
